import "./index.scss";

import { Box, Stack } from "@mui/material";
import { ReactComponent as AddIcon } from "assets/images/icons/add.svg";
import { ReactComponent as IconChevronDown } from "assets/images/square-buttons/chevron-down-blue.svg";
import { ReactComponent as IconChevronRight } from "assets/images/square-buttons/chevron-right-blue.svg";
import { ReactComponent as CheckedCircleIcon } from "assets/images/toggle-checkboxes/circle/on-normal.svg";
import SearchBar from "components/SearchBar";
import _ from "lodash";
import { FC, memo, useEffect, useState } from "react";

type selectedValueType = string | number;

export type TSearchableTreeNodeItem = {
  label: string;
  value?: selectedValueType;
  subItems?: TSearchableTreeNodeItem[];
};

interface SearchableTreeListProps {
  options: TSearchableTreeNodeItem[];
  callback: Function;
  addGroupAction?: Function;
  isSelectedCheckmarkDisplayed?: boolean;
  selectedValues?: selectedValueType[];
}

const SearchableTreeEntry = (props: {
  item: TSearchableTreeNodeItem;
  name: string;
  callback: Function;
  addGroupAction?: Function;
  forceExpand: boolean;
  isSelectedCheckmarkDisplayed?: boolean;
  selectedValues?: selectedValueType[];
}) => {
  const {
    item,
    name,
    callback,
    addGroupAction,
    forceExpand,
    isSelectedCheckmarkDisplayed,
    selectedValues,
  } = props;
  const [mySubNodeIsCollapsed, setMySubNodeIsCollapsed] = useState(true);

  useEffect(() => {
    setMySubNodeIsCollapsed(!forceExpand);
  }, [forceExpand]);

  let depth = 0;
  let spacerArray = [];
  if (item.subItems) {
    depth = name.split("_").length - 1;
    spacerArray = Array(depth).fill(0);
  }

  const addAllItems = (e) => {
    e.stopPropagation();
    addGroupAction(item.subItems);
  };

  return (
    <>
      <Stack
        direction={"row"}
        alignItems="center"
        gap={"10px"}
        className={`tree-node`}
        onClick={() => {
          if (item.subItems) {
            setMySubNodeIsCollapsed(!mySubNodeIsCollapsed);
          } else {
            callback(item);
          }
        }}
      >
        {item.subItems && mySubNodeIsCollapsed && <IconChevronRight />}
        {item.subItems && !mySubNodeIsCollapsed && <IconChevronDown />}
        <span className="label">{item.label}</span>
        <Stack direction={"row"} gap={"10px"}>
          {item?.subItems?.length && addGroupAction && <AddIcon onClick={addAllItems} />}
          {isSelectedCheckmarkDisplayed &&
            selectedValues?.length > 0 &&
            selectedValues.includes(item.value) && <CheckedCircleIcon />}
        </Stack>
      </Stack>
      {item.subItems && (
        <Stack direction={"row"} className={mySubNodeIsCollapsed ? `collapsed` : ``}>
          {spacerArray &&
            spacerArray.map((_e, i) => <Box key={`spacer${i}`} className={`spacer`}></Box>)}
          <Stack flexGrow={1}>
            {item.subItems.map((e, index) => (
              <SearchableTreeEntry
                item={e}
                key={`${name}_${index}`}
                name={`${name}_${index}`}
                callback={callback}
                forceExpand={forceExpand}
                isSelectedCheckmarkDisplayed={isSelectedCheckmarkDisplayed}
                selectedValues={selectedValues}
              />
            ))}
          </Stack>
        </Stack>
      )}
    </>
  );
};

const SearchableTreeList: FC<SearchableTreeListProps> = (props) => {
  const {
    options = [],
    callback,
    addGroupAction,
    isSelectedCheckmarkDisplayed,
    selectedValues,
  } = props;
  const [searchStr, setSearchStr] = useState("");
  let optionsWithValues = [];
  optionsWithValues = options.map((e) => {
    if (!e.value) {
      e.value = e.label;
    }
    return e;
  });
  const [filterOptions, setFilterOptions] = useState(optionsWithValues);

  const handleSearch = (str: string) => {
    function findByLabel(entry) {
      if (entry.label.toLowerCase().includes(str.toLowerCase())) {
        return true;
      }
      if (Array.isArray(entry.subItems)) {
        for (let i = 0; i < entry.subItems.length; i++) {
          if (findByLabel(entry.subItems[i])) {
            entry.subItems = [...entry.subItems.filter(findByLabel)];
            return true;
          }
        }
      }
      return false;
    }

    let optionsToSearchIn = _.cloneDeep(options);
    setFilterOptions([...optionsToSearchIn.filter(findByLabel)]);
    setSearchStr(str);
  };

  return (
    <Box className={`searchable-list-container`}>
      <Box className="header">
        <SearchBar className="search-bar" value={searchStr} onSearch={handleSearch} />
      </Box>
      <Box className="list tree-list">
        {filterOptions.map((item, index) => (
          <SearchableTreeEntry
            item={item}
            key={`treeEntry_${index}`}
            name={`treeEntry_${index}`}
            callback={callback}
            addGroupAction={addGroupAction}
            forceExpand={searchStr !== ""}
            isSelectedCheckmarkDisplayed={isSelectedCheckmarkDisplayed}
            selectedValues={selectedValues}
          />
        ))}
      </Box>
    </Box>
  );
};

export default memo(SearchableTreeList);
